import React from "react";
import { Box, Paper, Typography, Grid, Button, Chip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import useGetAirport from "../../hooks/airports/useGetAirport";
import WrapperSection from "../../components/global/WrapperSection";
import GlobalBreadcrumbs from "../../components/global/GlobalBreadcrumbs";
import LoadingPage from "../../components/global/LoadingPage";

const AirportDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng === "ar";

    const { airport, isLoading } = useGetAirport(id);

    if (isLoading) return <LoadingPage />;
    if (!airport) return <Typography>Not Found</Typography>;

    const isActive = airport?.status === "active";

    return (
        <WrapperSection>
            <GlobalBreadcrumbs />
            <Box sx={{ p: { xs: 2, md: 4 } }}>
                <Paper elevation={0} sx={{ p: 4, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                            {isArabic ? "تفاصيل المطار" : "Airport Details"}
                        </Typography>
                        <Button variant="outlined" onClick={() => navigate("/airports")}>
                            {isArabic ? "عـودة" : "Back"}
                        </Button>
                    </Box>

                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Typography variant="caption" color="text.secondary">
                                {isArabic ? "اسم المطار (عربي)" : "Airport Name (AR)"}
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                {airport?.name?.ar || "-"}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="caption" color="text.secondary">
                                {isArabic ? "اسم المطار (إنجليزي)" : "Airport Name (EN)"}
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                {airport?.name?.en || "-"}
                            </Typography>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Typography variant="caption" color="text.secondary">
                                {isArabic ? "الدولة" : "Country"}
                            </Typography>
                            <Typography variant="body1">
                                {airport?.country?.name?.[lng] || airport?.country?.name?.en || airport?.country?.name?.ar || "-"}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="caption" color="text.secondary">
                                {isArabic ? "المدينة" : "City"}
                            </Typography>
                            <Typography variant="body1">
                                {airport?.city?.name?.[lng] || airport?.city?.name?.en || airport?.city?.name?.ar || "-"}
                            </Typography>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Typography variant="caption" color="text.secondary">
                                {isArabic ? "الصالة (عربي)" : "Terminal (AR)"}
                            </Typography>
                            <Typography variant="body1">
                                {airport?.terminal?.ar || "-"}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="caption" color="text.secondary">
                                {isArabic ? "الصالة (إنجليزي)" : "Terminal (EN)"}
                            </Typography>
                            <Typography variant="body1">
                                {airport?.terminal?.en || "-"}
                            </Typography>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Typography variant="caption" color="text.secondary">
                                {isArabic ? "الحالة" : "Status"}
                            </Typography>
                            <Box sx={{ mt: 0.5 }}>
                                <Chip
                                    label={isArabic ? (isActive ? "نشط" : "معطل") : (isActive ? "Active" : "Disabled")}
                                    color={isActive ? "success" : "default"}
                                    size="small"
                                />
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="caption" color="text.secondary">
                                {isArabic ? "تاريخ الإضافة" : "Created At"}
                            </Typography>
                            <Typography variant="body1">
                                {new Date(airport?.created_at).toLocaleDateString(isArabic ? 'ar-EG' : 'en-US')}
                            </Typography>
                        </Grid>
                    </Grid>
                </Paper>
            </Box>
        </WrapperSection>
    );
};

export default AirportDetails;
